import { useEffect, useState } from "react"
import { BiscuitMini } from "@/components/biscuit/BiscuitMini"
import { cn } from "@/lib/utils"

interface BiscuitSkeletonProps {
  label?: string
  className?: string
}

const LOADING_QUIPS = [
  "Biscuit's fetching",
  "sniffing the data",
  "tail-wagging through pages",
  "rounding up your treats",
  "doing zoomies",
  "loading",
]

/**
 * Honors `prefers-reduced-motion`. Returns true only when the user has
 * explicitly opted in to reduced motion — defaults to false on SSR and
 * before the media query resolves.
 */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    const onChange = () => setReduced(mq.matches)
    onChange()
    mq.addEventListener?.("change", onChange)
    return () => mq.removeEventListener?.("change", onChange)
  }, [])
  return reduced
}

export function BiscuitSkeleton({ label, className }: BiscuitSkeletonProps) {
  const reducedMotion = usePrefersReducedMotion()
  const [quipIdx, setQuipIdx] = useState(0)
  useEffect(() => {
    if (label || reducedMotion) return // caller pinned a label, or user opted out of motion
    const t = setInterval(() => setQuipIdx((i) => (i + 1) % LOADING_QUIPS.length), 2200)
    return () => clearInterval(t)
  }, [label, reducedMotion])
  const visibleText = label ?? LOADING_QUIPS[quipIdx]
  // AT users hear a single stable label — not a chattering live region that
  // re-announces every 2.2s as the quip rotates.
  const ariaLabel = label ?? "Loading"

  return (
    <div
      className={cn("flex flex-col items-center justify-center py-10 gap-2", className)}
      role="status"
      aria-label={ariaLabel}
    >
      <BiscuitMini mood="sleepy" />
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground" aria-hidden="true">
        <span className="transition-opacity duration-300 motion-reduce:transition-none">{visibleText}</span>
        <span className="flex gap-0.5">
          <span className="w-1 h-1 rounded-full bg-muted-foreground animate-bounce motion-reduce:animate-none" style={{ animationDelay: "0ms" }} />
          <span className="w-1 h-1 rounded-full bg-muted-foreground animate-bounce motion-reduce:animate-none" style={{ animationDelay: "150ms" }} />
          <span className="w-1 h-1 rounded-full bg-muted-foreground animate-bounce motion-reduce:animate-none" style={{ animationDelay: "300ms" }} />
        </span>
      </div>
    </div>
  )
}

/**
 * Card-shaped shimmer placeholder for transaction/budget lists. Renders `count`
 * fake rows that look like a transaction card while data is loading.
 */
export function TransactionCardSkeleton({ count = 3, className }: { count?: number; className?: string }) {
  return (
    <div className={cn("space-y-2", className)} role="status" aria-label="Loading transactions">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          aria-hidden="true"
          className="p-4 rounded-xl border bg-card flex items-center gap-3 animate-pulse motion-reduce:animate-none"
          style={{ animationDelay: `${i * 100}ms` }}
        >
          <div className="w-14 h-14 rounded-full bg-muted shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-2/5 bg-muted rounded" />
            <div className="h-2.5 w-3/5 bg-muted/60 rounded" />
          </div>
          <div className="h-4 w-16 bg-muted rounded" />
        </div>
      ))}
    </div>
  )
}
